// Test Automation API Page
// Provides a comprehensive interface for automated testing

import React, { useState, useEffect } from 'react';
import { Play, Download, Trash2, RefreshCw, BarChart3, Clock, CheckCircle, XCircle, AlertCircle, Code, Database, Settings } from 'lucide-react';
import { testAutomationAPI, TestSuite, TestResult, TestSummary } from '../services/testAutomationAPI';

const TestAutomationAPI: React.FC = () => {
  const [testSuites, setTestSuites] = useState<TestSuite[]>([]);
  const [selectedSuite, setSelectedSuite] = useState<TestSuite | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'results' | 'api' | 'settings'>('dashboard');

  // Load test suites on mount
  useEffect(() => {
    loadTestSuites();
    loadStatistics();
  }, []);

  const loadTestSuites = () => {
    const suites = testAutomationAPI.getAllTestSuites();
    setTestSuites(suites);
    if (suites.length > 0 && !selectedSuite) {
      setSelectedSuite(suites[suites.length - 1]);
    }
  };

  const loadStatistics = () => {
    const stats = testAutomationAPI.getTestStatistics();
    setStatistics(stats);
  };

  const runTests = async () => {
    setIsRunning(true);
    try {
      // Check if we're on the test-api page and need to navigate
      if (window.location.pathname === '/test-api') {
        console.log('🧪 Test API: Navigating to main app first...');
        
        // Navigate to main app
        window.location.href = '/';
        
        // Wait for navigation and page load
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
      
      const suite = await testAutomationAPI.runAutomatedTests();
      loadTestSuites();
      loadStatistics();
      setSelectedSuite(suite);
    } catch (error) {
      console.error('Error running tests:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const clearAllTests = () => {
    testAutomationAPI.clearAllTestSuites();
    setTestSuites([]);
    setSelectedSuite(null);
    loadStatistics();
  };

  const exportResults = (suite?: TestSuite) => {
    const data = suite ? testAutomationAPI.exportTestResults(suite.id) : testAutomationAPI.exportTestResults();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-results-${suite ? suite.id : 'all'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'fail': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'skip': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass': return 'text-green-600 bg-green-50 border-green-200';
      case 'fail': return 'text-red-600 bg-red-50 border-red-200';
      case 'skip': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Code className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Test Automation API
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Programmatic testing and automation interface
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={runTests}
                disabled={isRunning}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="w-4 h-4 mr-2" />
                {isRunning ? 'Running...' : 'Run Tests'}
              </button>
              <button
                onClick={clearAllTests}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'results', label: 'Test Results', icon: Database },
              { id: 'api', label: 'API Interface', icon: Code },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            {statistics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Database className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Total Test Suites
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            {statistics.totalSuites}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Total Tests
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            {statistics.totalTests}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <BarChart3 className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Average Success Rate
                          </dt>
                          <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            {statistics.averageSuccessRate.toFixed(1)}%
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Clock className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            Last Run
                          </dt>
                          <dd className="text-sm text-gray-900 dark:text-gray-100">
                            {statistics.lastRun ? new Date(statistics.lastRun).toLocaleString() : 'Never'}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Test Suites */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Recent Test Suites
                </h3>
                {testSuites.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">No test suites found. Run tests to get started.</p>
                ) : (
                  <div className="space-y-3">
                    {testSuites.slice(-5).reverse().map((suite) => (
                      <div
                        key={suite.id}
                        onClick={() => setSelectedSuite(suite)}
                        className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          selectedSuite?.id === suite.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {suite.name}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(suite.summary.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {suite.summary.successRate.toFixed(1)}%
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {suite.summary.passedTests}/{suite.summary.totalTests} passed
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'results' && selectedSuite && (
          <div className="space-y-6">
            {/* Suite Summary */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                    {selectedSuite.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => exportResults(selectedSuite)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {selectedSuite.summary.totalTests}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Tests</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {selectedSuite.summary.passedTests}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Passed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {selectedSuite.summary.failedTests}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Failed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedSuite.summary.successRate.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Success Rate</div>
                  </div>
                </div>

                {/* Test Results */}
                <div className="space-y-2">
                  {selectedSuite.results.map((result, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 border rounded-lg ${getStatusColor(result.status)}`}
                    >
                      <div className="flex items-center">
                        {getStatusIcon(result.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium">{result.testName}</div>
                          <div className="text-xs opacity-75">{result.message}</div>
                        </div>
                      </div>
                      <div className="text-xs opacity-75">
                        {result.duration ? `${result.duration}ms` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                  API Interface
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Access the test automation API programmatically. The API is available globally as <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">window.testAutomationAPI</code>.
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Run Tests Programmatically</h4>
                    <pre className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg text-sm overflow-x-auto">
{`// Run automated tests
const suite = await window.testAutomationAPI.runAutomatedTests();
console.log('Test Results:', suite);`}
                    </pre>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Get Test Statistics</h4>
                    <pre className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg text-sm overflow-x-auto">
{`// Get test statistics
const stats = window.testAutomationAPI.getTestStatistics();
console.log('Statistics:', stats);`}
                    </pre>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Export Results</h4>
                    <pre className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg text-sm overflow-x-auto">
{`// Export all results as JSON
const json = window.testAutomationAPI.exportTestResults();
console.log('All Results:', json);

// Export specific suite
const suiteJson = window.testAutomationAPI.exportTestResults('suite_id');
console.log('Suite Results:', suiteJson);`}
                    </pre>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Available Methods</h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <li>• <code>runAutomatedTests()</code> - Run all tests and return results</li>
                      <li>• <code>getAllTestSuites()</code> - Get all test suites</li>
                      <li>• <code>getTestSuite(id)</code> - Get specific test suite</li>
                      <li>• <code>getTestStatistics()</code> - Get test statistics</li>
                      <li>• <code>exportTestResults(id?)</code> - Export results as JSON</li>
                      <li>• <code>clearAllTestSuites()</code> - Clear all test data</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Settings
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Configure test automation settings and preferences.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Auto-run tests on page load
                    </label>
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Automatically run tests when the page loads
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Test timeout (seconds)
                    </label>
                    <input
                      type="number"
                      defaultValue={30}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Maximum time to wait for tests to complete
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Retry failed tests
                    </label>
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Automatically retry failed tests once
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestAutomationAPI;
